import { App, Card, Collapse } from "antd";
import { FC, memo, useCallback, useMemo, useState } from "react";
import {
    deleteOrderRemove,
    getApiError,
    postOrderCreateByPoint,
    postOrderSaveOne,
} from "tm-shared/api";
import { CheckoutOrder, OrderType, Price } from "tm-shared/types";

import { useAppDispatch } from "@/hooks/redux";
import { removeYesterdayCheckoutItem } from "@/store/slices/work";

import { ProductRemainingsMap } from "../YesterdayOpenedCheckouts.types";
import { CheckoutDiffItem, RemainingResolution } from "./CheckoutCard.types";
import { CheckoutCardContent } from "./CheckoutCardContent";
import { CheckoutCardLabel } from "./CheckoutCardLabel";

interface CheckoutCardProps {
    checkout: CheckoutOrder;
    orderTypeVocabulary: Record<OrderType, string>;
    submitting: boolean;
    remainings: ProductRemainingsMap;
    prices: Record<string, Price>;
    hasLoadError: boolean;
    initialLoading: boolean;
    setSubmitting: (value: boolean) => void;
    onAfterMove: (pointId: string) => Promise<void>;
}

export const CheckoutCard: FC<CheckoutCardProps> = memo(
    ({
        checkout,
        setSubmitting,
        submitting,
        orderTypeVocabulary,
        remainings,
        prices,
        hasLoadError,
        initialLoading,
        onAfterMove,
    }) => {
        const dispatch = useAppDispatch();
        const { notification } = App.useApp();

        const [removing, setRemoving] = useState(false);
        const [moving, setMoving] = useState(false);
        const [remainingResolutions, setRemainingResolutions] = useState<
            Record<string, RemainingResolution | undefined>
        >({});

        const { products, orderId, id, orderType, pointId, paymentForm } =
            checkout;

        const productEntries = useMemo(
            () => Object.entries(products ?? {}),
            [products],
        );

        const diffItems = useMemo<CheckoutDiffItem[]>(() => {
            if (orderType === OrderType.R) return [];

            return productEntries.reduce<CheckoutDiffItem[]>(
                (acc, [barcode, product]) => {
                    const productName = product.name ?? barcode;
                    const orderedCount = product.count ?? 0;
                    const oldPrice = product.price ?? 0;
                    const actualPrice = prices[barcode]?.price;
                    const remaining = remainings[barcode];

                    const priceChanged =
                        typeof actualPrice === "number" &&
                        actualPrice !== oldPrice
                            ? { oldPrice, newPrice: actualPrice }
                            : undefined;

                    const insufficientRemaining =
                        typeof remaining === "number" &&
                        orderedCount > remaining
                            ? { orderedCount, remaining }
                            : undefined;

                    if (!priceChanged && !insufficientRemaining) return acc;

                    acc.push({
                        barcode,
                        productName,
                        priceChanged,
                        insufficientRemaining,
                    });
                    return acc;
                },
                [],
            );
        }, [orderType, productEntries, prices, remainings]);

        const insufficientRemainingItems = useMemo(
            () =>
                diffItems.filter(
                    ({ insufficientRemaining }) => !!insufficientRemaining,
                ),
            [diffItems],
        );

        const unresolvedRemainings = useMemo(
            () =>
                insufficientRemainingItems.filter(
                    ({ barcode }) => !remainingResolutions[barcode],
                ),
            [insufficientRemainingItems, remainingResolutions],
        );

        const hasAnyDiffs = diffItems.length > 0;
        const hasRemainingConflicts = insufficientRemainingItems.length > 0;
        const allRemainingsResolved = unresolvedRemainings.length === 0;

        const handleRemainingResolutionChange = useCallback(
            (barcode: string, value: RemainingResolution) => {
                setRemainingResolutions((prev) => ({
                    ...prev,
                    [barcode]: value,
                }));
            },
            [],
        );

        const handleDelete = useCallback(async () => {
            try {
                setSubmitting(true);
                setRemoving(true);

                await deleteOrderRemove({ orderId });

                dispatch(removeYesterdayCheckoutItem(orderId));
            } catch (error) {
                const { displayMessage } = getApiError(error);
                notification.error({
                    title: "Не вдалося видалити",
                    description: displayMessage,
                });
            } finally {
                setSubmitting(false);
                setRemoving(false);
            }
        }, [notification, dispatch, orderId, setSubmitting]);

        const handleMove = useCallback(async () => {
            try {
                setSubmitting(true);
                setMoving(true);

                const productsToRemove = Object.entries(remainingResolutions)
                    .filter(([, r]) => r === RemainingResolution.RemoveProduct)
                    .map(([barcode]) => barcode);

                const ignoreRemainings =
                    Object.entries(remainingResolutions).filter(
                        ([, r]) => r === RemainingResolution.IgnoreRemaining,
                    ).length > 0;

                const { result: newOrderId } = await postOrderCreateByPoint({
                    pointId,
                    type: orderType,
                });

                const updatedProducts = Object.values(products)
                    .filter(
                        ({ barcode }) => !productsToRemove.includes(barcode),
                    )
                    .map((product) => {
                        const { barcode, count } = product;
                        const resolution = remainingResolutions[barcode];

                        if (
                            resolution === RemainingResolution.ReduceToRemaining
                        ) {
                            return {
                                ...product,
                                count: remainings[barcode] ?? count,
                            };
                        }

                        return product;
                    });

                await postOrderSaveOne({
                    orderId: newOrderId,
                    products: updatedProducts,
                    paymentForm,
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    //@ts-expect-error
                    context: { ignoreRemainings },
                });

                await deleteOrderRemove({ orderId }).catch((error) =>
                    console.error(error),
                );

                dispatch(removeYesterdayCheckoutItem(orderId));

                await onAfterMove(pointId);
            } catch (error) {
                const { displayMessage } = getApiError(error);
                notification.error({
                    title: "Не вдалося перенести замовлення",
                    description: displayMessage,
                });
            } finally {
                setSubmitting(false);
                setMoving(false);
            }
        }, [
            notification,
            setSubmitting,
            remainingResolutions,
            pointId,
            orderType,
            products,
            paymentForm,
            orderId,
            dispatch,
            onAfterMove,
            remainings,
        ]);

        const moveDisable =
            submitting ||
            initialLoading ||
            hasLoadError ||
            !allRemainingsResolved;

        return (
            <Card
                size="small"
                style={{ width: "100%" }}
                styles={{ body: { padding: 0 } }}
            >
                <Collapse
                    items={[
                        {
                            key: id,
                            label: (
                                <CheckoutCardLabel
                                    checkout={checkout}
                                    orderTypeVocabulary={orderTypeVocabulary}
                                    submitting={submitting}
                                    removing={removing}
                                    moving={moving}
                                    moveDisable={moveDisable}
                                    onDelete={handleDelete}
                                    onMove={handleMove}
                                />
                            ),
                            children: (
                                <CheckoutCardContent
                                    hasLoadError={hasLoadError}
                                    hasAnyDiffs={hasAnyDiffs}
                                    hasRemainingConflicts={
                                        hasRemainingConflicts
                                    }
                                    allRemainingsResolved={
                                        allRemainingsResolved
                                    }
                                    diffItems={diffItems}
                                    remainingResolutions={remainingResolutions}
                                    onResolutionChange={
                                        handleRemainingResolutionChange
                                    }
                                />
                            ),
                        },
                    ]}
                />
            </Card>
        );
    },
);

CheckoutCard.displayName = "CheckoutCard";
